/**
 * Read a design token (CSS custom property) at runtime — used by canvas rendering
 * (the waveform) where colors can't be applied via className. Keeps tokens.css the
 * single source of truth: no hex ever appears in TS.
 */
export function token(name: string): string {
  if (typeof getComputedStyle === 'undefined') return '';
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

export type ChipTone = 'pending' | 'processing' | 'review' | 'complete' | 'failed' | 'sold' | 'neutral';

import type { AssetProcessingState, JobState, ListingStatus, SubmissionStatus } from './types';

/** Map a raw job state to the design system's five job chips (§5.6). */
export function jobStateToChip(state: JobState): ChipTone {
  switch (state) {
    case 'pending':
    case 'enqueued':
      return 'pending';
    case 'running':
    case 'awaiting_callback':
      return 'processing';
    case 'awaiting_user_input':
      return 'review';
    case 'complete':
      return 'complete';
    case 'failed':
    case 'dlq':
      return 'failed';
    default:
      return 'neutral';
  }
}

/** Map an asset processing_state to the same chip vocabulary. */
export function assetStateToChip(state: AssetProcessingState): ChipTone {
  switch (state) {
    case 'pending':
      return 'pending';
    case 'processing':
      return 'processing';
    case 'awaiting_review':
      return 'review';
    case 'ready':
      return 'complete';
    case 'failed':
      return 'failed';
    case 'archived':
      return 'neutral';
    default:
      return 'neutral';
  }
}

export function listingStatusToChip(status: ListingStatus): ChipTone {
  switch (status) {
    case 'published':
      return 'complete';
    case 'draft':
    case 'pending_review':
      return 'pending';
    case 'paused':
      return 'processing';
    default:
      return 'neutral';
  }
}

/** Map a submission status to the shared chip vocabulary (inbox/outbox badges). */
export function submissionStatusToChip(status: SubmissionStatus): ChipTone {
  switch (status) {
    case 'pending':
      return 'pending';
    case 'accepted':
      return 'complete';
    case 'declined':
      return 'failed';
    case 'withdrawn':
      return 'neutral';
    default:
      return 'neutral';
  }
}
